import { DateTime } from "luxon";

import Typography from "@mui/material/Typography";

import CheckoutLineItem from "@app/analytics/account/Billing/components/CheckoutLineItem";
import ExitCheckoutButton from "@app/analytics/account/Billing/components/ExitCheckoutButton";
import { OPTS_BY_PLAN } from "@app/analytics/account/Billing/components/PlanDetail/constants";
import type { NewSubscription } from "@app/analytics/account/Billing/types";
import {
  getPlanLabel,
  getPlanPriceByPlanAndCadence,
  getRenewalDescription,
} from "@app/analytics/account/Billing/utils";
import useBillingContext from "@app/analytics/hooks/billing/useBillingContext";
import ROUTES from "@app/common/routes";
import type { PreviewInvoice } from "@app/common/types";
import { pluralize } from "@app/common/utils";
import { formatNumber, titleize } from "@app/utils/utils";

import Stack, { Row } from "@rui/Stack";
import TooltipIcon from "@rui/TooltipIcon";

import { BreakdownHighlight, LineItemDivider } from "../styles";

interface BillingSubscriptionCheckoutBreakdownProps {
  invoice: PreviewInvoice | null;
  isLoading: boolean;
  subscription: NewSubscription;
}

const fromCents = (cents: number | null) => (cents ?? 0) / 100;
const formatDate = (date: DateTime) =>
  date.toLocaleString({
    month: "long",
    day: "numeric",
    year: "numeric",
  });

const BillingSubscriptionCheckoutBreakdown = ({
  invoice,
  isLoading,
  subscription,
}: BillingSubscriptionCheckoutBreakdownProps) => {
  const { isEligibleForLegacyPricing, subscription: currentSubscription } =
    useBillingContext();

  // this will never happen, but include to appease the types in the render
  if (subscription.plan === "unknown") return null;

  const planLabel = getPlanLabel(subscription.plan);

  const pricePerUser =
    getPlanPriceByPlanAndCadence({
      cadence: subscription.cadence,
      plan: subscription.plan,
      useLegacyPrices: isEligibleForLegacyPricing,
    }) * (subscription.cadence === "monthly" ? 1 : 12);

  const dueDate = invoice?.periodStart;
  const renewalDate = invoice?.periodEnd;
  const rawSubtotal = pricePerUser * subscription.quantity;

  const credits = invoice
    ? rawSubtotal + fromCents(invoice.tax) - fromCents(invoice.total)
    : 0;

  return (
    <Stack variant="center" flex={1} sx={{ width: "100%", p: 2 }}>
      <Stack
        variant="left"
        sx={{ width: "100%", maxWidth: "480px" }}
        spacing={1.5}
      >
        <ExitCheckoutButton to={ROUTES.BILLING_COMPARE_PLANS}>
          Change plan
        </ExitCheckoutButton>
        <Stack spacing={0.5} sx={{ pb: 1 }}>
          <Row variant="left" spacing={1}>
            <Typography variant="h1">Read {planLabel}</Typography>
          </Row>
          <Typography
            variant="bodySecondaryMed"
            sx={{ color: "transparent.white.60" }}
          >
            {OPTS_BY_PLAN[subscription.plan].description}
          </Typography>
        </Stack>
        <BreakdownHighlight spacing={0.5}>
          <CheckoutLineItem
            label={`${planLabel} - ${titleize(subscription.cadence)}`}
            value={pricePerUser}
          />
          <Row variant="apart">
            <Typography>Licenses</Typography>
            <Typography variant="bodyPrimaryMed">
              {formatNumber(subscription.quantity)}{" "}
              {pluralize("license", subscription.quantity)}
            </Typography>
          </Row>
        </BreakdownHighlight>
        <Stack sx={{ p: 2, width: "100%" }} spacing={1}>
          <CheckoutLineItem label="Subtotal" value={rawSubtotal} />
          <CheckoutLineItem
            label={
              <Row variant="left" spacing={1}>
                <Typography>Credits</Typography>
                <TooltipIcon
                  title="Credits are applied when upgrading from one paid plan to another, to reflect the prorated amount."
                  placement="bottom-start"
                />
              </Row>
            }
            isLoading={isLoading}
            value={invoice ? -credits : undefined}
            zeroValue="-"
          />
          <LineItemDivider />
          <CheckoutLineItem
            testId="billing-sales-tax"
            label="Sales tax"
            isLoading={isLoading}
            unit="cents"
            value={invoice ? invoice.tax ?? 0 : undefined}
          />
          <LineItemDivider />
          <CheckoutLineItem
            label={`Total${dueDate ? ` due ${formatDate(dueDate)}` : ""}`}
            isLoading={isLoading}
            unit="cents"
            value={invoice?.total}
          />
          {renewalDate && (
            <Row variant="center" sx={{ pt: 2, textAlign: "center" }}>
              <Typography
                variant="bodySecondaryReg"
                sx={{ color: "transparent.white.60" }}
              >
                {getRenewalDescription({
                  dueDate,
                  inTrialPeriod: currentSubscription.inTrialPeriod,
                  plan: subscription.plan,
                  renewalDate,
                })}
              </Typography>
            </Row>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default BillingSubscriptionCheckoutBreakdown;
